import React, { useState, useEffect } from 'react';
import { CATEGORIES } from '../lib/scorecard-config.js';
import { API_URL } from '../lib/config.js';

const ATTR_MAP = {};
CATEGORIES.forEach(cat => cat.attributes.forEach(a => { ATTR_MAP[a.id] = { label: a.label, weight: a.weight, category: cat.name }; }));

const CAT_STYLES = {
  'Verification & Account Handling': { border: 'border-l-emerald-500', icon: 'ti-shield-check', iconColor: 'text-emerald-600' },
  'Accuracy & Knowledge': { border: 'border-l-purple-500', icon: 'ti-book', iconColor: 'text-purple-600' },
  'Resolution & Effort': { border: 'border-l-blue-500', icon: 'ti-target', iconColor: 'text-blue-600' },
  'Empathy & Tone': { border: 'border-l-pink-500', icon: 'ti-heart', iconColor: 'text-pink-600' },
  'Communication & Clarity': { border: 'border-l-amber-500', icon: 'ti-message-dots', iconColor: 'text-amber-600' },
  'Responsiveness & Documentation': { border: 'border-l-cyan-500', icon: 'ti-clock', iconColor: 'text-cyan-600' },
};

const PILL = {
  'Meets': 'bg-emerald-50 text-emerald-700 border-emerald-300',
  'Partial': 'bg-amber-50 text-amber-700 border-amber-300',
  'Misses': 'bg-red-50 text-red-700 border-red-300',
  'N/A': 'bg-gray-100 text-gray-500 border-gray-200',
};

const BAND_STYLES = {
  'Excellent': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Good': 'bg-blue-50 text-blue-700 border-blue-200',
  'Okay': 'bg-amber-50 text-amber-700 border-amber-200',
  'Needs improvement': 'bg-orange-50 text-orange-700 border-orange-200',
  'Failed': 'bg-red-50 text-red-700 border-red-200',
};

export default function PublicScorecard({ evalId }) {
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAppeal, setShowAppeal] = useState(false);
  const [appealItems, setAppealItems] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetch(API_URL + '/scorecard/view?id=' + evalId)
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setEv(d); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [evalId]);

  const toggleAppealItem = (attrId) => {
    setAppealItems(prev => {
      const copy = { ...prev };
      if (copy[attrId]) delete copy[attrId];
      else copy[attrId] = { reason: '' };
      return copy;
    });
  };

  const setAppealReason = (attrId, reason) => {
    setAppealItems(prev => ({ ...prev, [attrId]: { ...prev[attrId], reason } }));
  };

  const handleSubmitAppeal = async () => {
    const selected = Object.entries(appealItems).filter(([, v]) => v.reason.trim());
    if (selected.length === 0) { alert('Please select at least one attribute and provide a reason.'); return; }
    setSubmitting(true);
    try {
      const appealed_attributes = selected.map(([id, v]) => ({
        id,
        current_rating: ev.scores?.[id] || 'Unknown',
        reason: v.reason.trim(),
      }));
      await fetch(API_URL + '/appeal/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          evaluation_id: ev.id,
          agent_name: ev.agent_name,
          ticket_number: ev.ticket_number,
          ticket_subject: ev.ticket_subject,
          original_score: ev.final_score,
          rating_band: ev.rating_band,
          appealed_attributes,
        }),
      });
      setSubmitted(true);
    } catch (e) { alert('Failed to submit: ' + e.message); }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-400">Loading scorecard...</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center max-w-md">
        <i className="ti ti-alert-circle text-3xl text-red-400" aria-hidden="true"></i>
        <p className="text-gray-600 mt-2">{error}</p>
      </div>
    </div>
  );

  if (!ev) return null;

  const scores = ev.scores || {};
  const appealableAttrs = [];
  CATEGORIES.forEach(cat => {
    cat.attributes.forEach(a => {
      const rating = scores[a.id];
      if (rating === 'Partial' || rating === 'Misses') {
        appealableAttrs.push({ ...a, rating, category: cat.name });
      }
    });
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <i className="ti ti-clipboard-check text-purple-600 text-lg" aria-hidden="true"></i>
          <h1 className="text-base font-medium">QA Scorecard</h1>
          <span className="text-xs text-gray-400 ml-1">Sabrent Support</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Score hero */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <div className="flex flex-wrap items-center gap-6">
            <div className="text-center">
              <div className={'text-5xl font-medium ' + (ev.autofail_triggered ? 'text-red-500' : 'text-gray-900')}>
                {Math.round((ev.final_score || 0) * 100)}<span className="text-xl text-gray-400">%</span>
              </div>
              <span className={'mt-2 inline-block text-xs font-medium px-3 py-1 rounded-full border ' + (BAND_STYLES[ev.rating_band] || '')}>{ev.rating_band}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">#{ev.ticket_number} <span className="font-normal text-gray-500">— {ev.ticket_subject}</span></div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                <span><i className="ti ti-user text-xs mr-1" aria-hidden="true"></i>{ev.agent_name}</span>
                <span><i className="ti ti-calendar text-xs mr-1" aria-hidden="true"></i>{ev.eval_date}</span>
                {ev.customer_name && <span><i className="ti ti-users text-xs mr-1" aria-hidden="true"></i>{ev.customer_name}</span>}
              </div>
              {ev.appeal_adjusted && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-purple-600">
                  <i className="ti ti-refresh text-sm" aria-hidden="true"></i> Score adjusted via appeal
                </div>
              )}
            </div>
            <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">{ev.agent_name}</span>
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-3 mb-4">
          {CATEGORIES.map(cat => {
            const s = CAT_STYLES[cat.name] || {};
            const catAttrs = cat.attributes.map(a => ({ ...a, rating: scores[a.id] || 'N/A' }));
            const catApplicable = catAttrs.reduce((sum, a) => a.rating === 'N/A' ? sum : sum + a.weight, 0);
            const catEarned = catAttrs.reduce((sum, a) => {
              if (a.rating === 'N/A') return sum;
              if (a.rating === 'Meets') return sum + a.weight;
              if (a.rating === 'Partial') return sum + a.weight * 0.5;
              return sum;
            }, 0);
            const catPct = catApplicable > 0 ? Math.round((catEarned / catApplicable) * 100) : null;

            return (
              <div key={cat.name} className={'bg-white rounded-xl border border-gray-200 overflow-hidden border-l-[3px] ' + (s.border || '')}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-medium flex items-center gap-2">
                    <i className={'ti ' + (s.icon || '') + ' ' + (s.iconColor || '')} aria-hidden="true"></i>
                    {cat.name}
                  </h3>
                  {catPct != null && <span className="text-xs text-gray-500">{catPct}%</span>}
                </div>
                {catAttrs.map(attr => (
                  <div key={attr.id} className="px-4 py-2.5 border-b border-gray-50 last:border-0 flex items-center gap-2">
                    <span className="flex-1 text-sm text-gray-700">{attr.label} <span className="text-[11px] text-gray-400">({attr.weight})</span></span>
                    <span className={'px-2.5 py-1 rounded-full text-[11px] font-medium border ' + (PILL[attr.rating] || PILL['N/A'])}>{attr.rating}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Notes */}
        {ev.notes && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <h3 className="text-sm font-medium flex items-center gap-2 mb-2">
              <i className="ti ti-notes text-gray-400" aria-hidden="true"></i> Evaluator notes
            </h3>
            <p className="text-sm text-gray-600">{ev.notes}</p>
          </div>
        )}

        {/* Appeal section */}
        {submitted ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
            <i className="ti ti-check text-3xl text-emerald-600" aria-hidden="true"></i>
            <h3 className="text-lg font-medium text-emerald-800 mt-2">Appeal submitted</h3>
            <p className="text-sm text-emerald-600 mt-1">Your QA analyst has been notified and will review your appeal.</p>
          </div>
        ) : !showAppeal ? (
          appealableAttrs.length > 0 && (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500 mb-3">Disagree with your score?</p>
              <button onClick={() => setShowAppeal(true)}
                className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium inline-flex items-center gap-2">
                <i className="ti ti-message-circle" aria-hidden="true"></i> Submit an appeal
              </button>
            </div>
          )
        ) : (
          <div className="bg-white rounded-xl border border-purple-200 p-5">
            <h3 className="text-base font-medium mb-1">Submit an appeal</h3>
            <p className="text-xs text-gray-500 mb-4">Select the items you disagree with and explain why.</p>

            <div className="space-y-3 mb-4">
              {appealableAttrs.map(attr => (
                <div key={attr.id} className={'rounded-lg border p-3 transition cursor-pointer ' + (appealItems[attr.id] ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-gray-300')}
                  onClick={() => toggleAppealItem(attr.id)}>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={!!appealItems[attr.id]} onChange={() => {}}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">{attr.label}</div>
                      <div className="text-[11px] text-gray-400 mt-0.5">{attr.category}</div>
                    </div>
                    <span className={'px-2.5 py-1 rounded-full text-[11px] font-medium border ' + (PILL[attr.rating] || '')}>{attr.rating}</span>
                  </div>
                  {appealItems[attr.id] && (
                    <div className="mt-3" onClick={e => e.stopPropagation()}>
                      <textarea value={appealItems[attr.id]?.reason || ''} onChange={e => setAppealReason(attr.id, e.target.value)}
                        rows={2} placeholder="Why should this rating be changed?"
                        className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-400 resize-none" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <button onClick={handleSubmitAppeal} disabled={submitting}
                className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 text-sm font-medium transition">
                {submitting ? 'Submitting...' : 'Submit appeal'}
              </button>
              <button onClick={() => { setShowAppeal(false); setAppealItems({}); }}
                className="px-5 py-2.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
